import { Controller } from "@hotwired/stimulus"

export default class extends Controller<HTMLElement> {
  static targets = ["content", "cta", "toggle"]
  static values = { lines: { type: Number, default: 4 } }

  declare readonly contentTarget: HTMLElement
  declare readonly ctaTarget: HTMLElement
  declare readonly toggleTarget: HTMLElement
  declare readonly linesValue: number

  private collapsedHeight = 0

  connect() {
    if (this.overflows()) {
      this.collapsedHeight = this.contentTarget.clientHeight
      this.contentTarget.style.maxHeight = `${this.collapsedHeight}px`
      return
    }

    if (this.contentTarget.clientHeight <= this.lineLimit() + 1) {
      this.hideToggle()
      return
    }

    // line-clamp can't clamp block children; clamp by height instead
    this.collapsedHeight = this.lineLimit()
    this.element.classList.add("expandable--clip")
    this.contentTarget.style.maxHeight = `${this.collapsedHeight}px`
  }

  toggle(event: Event) {
    event.preventDefault()
    if (this.element.classList.contains("is-expanded")) this.collapse()
    else this.expand()
  }

  private overflows() {
    return this.contentTarget.scrollHeight > this.contentTarget.clientHeight + 1
  }

  private lineLimit() {
    const lineHeight = parseFloat(getComputedStyle(this.contentTarget).lineHeight) || 0
    return lineHeight * this.linesValue
  }

  private hideToggle() {
    this.ctaTarget.classList.add("hidden")
    this.element.classList.add("is-expanded")
    this.toggleTarget.setAttribute("aria-expanded", "true")
  }

  private expand() {
    this.element.classList.add("is-expanded")
    this.toggleTarget.setAttribute("aria-expanded", "true")
    this.contentTarget.style.maxHeight = `${this.contentTarget.scrollHeight}px`
  }

  private collapse() {
    this.contentTarget.style.maxHeight = `${this.contentTarget.scrollHeight}px`
    this.contentTarget.addEventListener(
      "transitionend",
      (event) => {
        if (event.target === this.contentTarget && event.propertyName === "max-height") {
          this.element.classList.remove("is-expanded")
          this.toggleTarget.setAttribute("aria-expanded", "false")
        }
      },
      { once: true },
    )

    // Reading offsetHeight forces a reflow so the max-height change below animates.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- the read is the side effect
    this.contentTarget.offsetHeight
    this.contentTarget.style.maxHeight = `${this.collapsedHeight}px`
  }
}
